import { defaultParseSearch } from '@tanstack/react-router';

export type AffectedTarget =
    | { kind: 'none' }
    | { kind: 'ad'; adNumber: string }
    | { kind: 'componentPartNumber'; projectId: number; partNumber: string; serialNumber?: string | null }
    | { kind: 'component'; componentId: number; serialNumber: string | null }
    | { kind: 'aircraft'; aircraftId: number }
    | { kind: 'project'; projectId: number }
    | { kind: 'collection'; collectionId: number };

const NONE: AffectedTarget = { kind: 'none' };

const AD_DETAIL = /\/subfolders\/(?:C006|B004)\/([^/]+)\/?$/;
const AD_LIST = /\/subfolders\/C006\/?$/;
const COMPONENT_DETAIL = /\/subfolders\/(?:B013|H001)\/(\d+)\/?$/;
const LEASE_COMPONENT = /^\/lease\/projects\/(\d+)\/components\/([^/]+)\/page\/?$/;
const AIRCRAFT_PAGE = /^\/aircrafts\/(\d+)(?:\/|$)/;
const ACTIVITY_PAGE = /^\/(?:line_items|removal_installation_list)\/\d+(?:\/|$)/;
const PROJECT_PAGES: readonly RegExp[] = [
    /^\/asset_collection(?:\/\d+)?\/assets\/\d+\/projects\/(\d+)(?:\/|$)/,
    /^\/lease\/projects\/(\d+)(?:\/|$)/,
    /^\/iata-binder\/projects\/(\d+)(?:\/|$)/,
];
const ASSET_BINDER = /^\/asset-binder\/?$/;
const COLLECTION_PAGE = /^\/asset_collection\/(\d+)\/assets\/?$/;

type SearchParams = Record<string, unknown>;

function toPositiveInt(raw: unknown): number | null {
    if (typeof raw === 'number') return Number.isSafeInteger(raw) && raw > 0 ? raw : null;
    if (typeof raw !== 'string' || !/^\d+$/.test(raw)) return null;
    const value = Number(raw);
    return value > 0 ? value : null;
}

/** Text value of a search param; the router parses all-digit values as numbers. */
function searchText(params: SearchParams, key: string): string | null {
    const raw = params[key];
    if (typeof raw !== 'string' && typeof raw !== 'number') return null;
    const trimmed = String(raw).trim();
    return trimmed === '' ? null : trimmed;
}

function decodeSegment(raw: string): string | null {
    let decoded = raw;
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        // Malformed escape sequence: keep the raw text.
    }
    const trimmed = decoded.trim();
    return trimmed === '' ? null : trimmed;
}

function matchAd(pathname: string, params: SearchParams): AffectedTarget | null {
    const detail = AD_DETAIL.exec(pathname);
    if (detail) {
        const adNumber = decodeSegment(detail[1]);
        return adNumber === null ? null : { kind: 'ad', adNumber };
    }
    if (!AD_LIST.test(pathname)) return null;
    const selected = searchText(params, 'selectedAD');
    return selected === null ? null : { kind: 'ad', adNumber: selected };
}

function matchComponent(pathname: string, params: SearchParams): AffectedTarget | null {
    const lease = LEASE_COMPONENT.exec(pathname);
    if (lease) {
        const partNumber = decodeSegment(lease[2]);
        const projectId = toPositiveInt(lease[1]);
        if (partNumber === null || projectId === null) return null;
        return { kind: 'componentPartNumber', projectId, partNumber };
    }
    const detail = COMPONENT_DETAIL.exec(pathname);
    const componentId = toPositiveInt(detail?.[1]);
    if (componentId === null) return null;
    return { kind: 'component', componentId, serialNumber: searchText(params, 'serialNumber') };
}

function matchAircraft(pathname: string, params: SearchParams): AffectedTarget | null {
    const page = AIRCRAFT_PAGE.exec(pathname);
    const pageId = toPositiveInt(page?.[1]);
    if (pageId !== null) return { kind: 'aircraft', aircraftId: pageId };
    if (!ACTIVITY_PAGE.test(pathname)) return null;
    const aircraftId = toPositiveInt(params.aircraftId);
    return aircraftId === null ? null : { kind: 'aircraft', aircraftId };
}

function matchProject(pathname: string, params: SearchParams): AffectedTarget | null {
    for (const pattern of PROJECT_PAGES) {
        const projectId = toPositiveInt(pattern.exec(pathname)?.[1]);
        if (projectId !== null) return { kind: 'project', projectId };
    }
    if (!ASSET_BINDER.test(pathname)) return null;
    const projectId = toPositiveInt(params.projectId);
    return projectId === null ? null : { kind: 'project', projectId };
}

function matchCollection(pathname: string): AffectedTarget | null {
    const collectionId = toPositiveInt(COLLECTION_PAGE.exec(pathname)?.[1]);
    return collectionId === null ? null : { kind: 'collection', collectionId };
}

export function matchAffectedTarget(pathname: string, search: string): AffectedTarget {
    const params = defaultParseSearch(search);
    return (
        matchAd(pathname, params) ??
        matchComponent(pathname, params) ??
        matchAircraft(pathname, params) ??
        matchProject(pathname, params) ??
        matchCollection(pathname) ??
        NONE
    );
}
